import { Component, computed, inject, linkedSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'; // cspell:disable-line
import { DialogModule } from 'primeng/dialog'; // cspell:disable-line
import { ButtonModule } from 'primeng/button'; // cspell:disable-line
import { RadioButtonModule } from 'primeng/radiobutton'; // cspell:disable-line
import { LucideAngularModule } from 'lucide-angular';
import { FormatCO2ePipe } from 'src/app/pipes/format-co2e.pipe';
import { DurationFormatPipe } from 'src/app/pipes/duration-format.pipe';
import { TransportAlternativeTipData } from '../../../models/reduction-tip';
import { ALTERNATIVE_ICON } from '../../../constants/reduction-tip-icons';
import { ReductionTipsService } from '../../../services/reduction-tips.service';
import { RouteLegsComponent } from '../route-legs/route-legs.component';

/**
 * The itineraries Google Maps found for one mode of one flight, side by side.
 * The page chips compare the modes; this is where the traveller compares the
 * journeys within one — their emissions, how long they take, which vehicles
 * they ride — and picks the one to simulate and, later, apply.
 */
@Component({
  selector: 'app-reduction-routes-dialog',
  standalone: true,
  imports: [
    FormsModule,
    TranslocoModule,
    DialogModule,
    ButtonModule,
    RadioButtonModule,
    LucideAngularModule,
    FormatCO2ePipe,
    DurationFormatPipe,
    RouteLegsComponent,
  ],
  templateUrl: './reduction-routes-dialog.component.html',
})
export class ReductionRoutesDialogComponent {
  protected tipsService = inject(ReductionTipsService);
  private translocoService = inject(TranslocoService);

  tip = computed(() => {
    const id = this.tipsService.routesDialog()?.tipId;
    return (
      this.tipsService
        .tips()
        .find((tip) => tip.id === id && tip.type === 'transport_alternative') ??
      null
    );
  });

  mode = computed(() => this.tipsService.routesDialog()?.mode ?? null);

  data = computed(
    () => (this.tip()?.data as TransportAlternativeTipData | undefined) ?? null,
  );

  visible = computed(() => this.itineraries().length > 0);

  icon = computed(() => {
    const mode = this.mode();
    return mode ? ALTERNATIVE_ICON[mode] : 'route';
  });

  /** "Train : itinéraires pour Aller Paris → Nice". */
  title = computed(() => {
    const tip = this.tip();
    const mode = this.mode();
    if (!tip || !mode) return '';
    return this.translocoService.translate('reductionTips.dialog.title', {
      mode: this.tipsService.alternativeLabel(mode, null),
      flight: this.tipsService.elementLabelOf(tip),
    });
  });

  /**
   * The itinerary the radios show: the one the page simulates when it is on
   * this mode, else the default one — until the user ticks another. Reset each
   * time the dialog opens, `routesDialog` being a new object every time.
   */
  chosenRouteIndex = linkedSignal({
    source: this.tipsService.routesDialog,
    computation: (dialog) => {
      const choice = dialog && this.tipsService.choiceOf(dialog.tipId);
      return choice && choice.mode === dialog.mode ? choice.routeIndex : 0;
    },
  });

  /** "par voyageur" or "pour les 8 voyageurs" — the basis every figure is on. */
  basis = computed(() =>
    this.tipsService.perPerson()
      ? this.translocoService.translate('reductionTips.dialog.perTraveller')
      : this.translocoService.translate('reductionTips.dialog.forGroup', {
          count: this.tipsService.travellers(),
        }),
  );

  flightEmissions = computed(() => {
    const data = this.data();
    return data
      ? this.tipsService.fromPerPerson(data.currentEmissionsPerPerson)
      : 0;
  });

  /**
   * The mode's itineraries, in the API's order — fastest first, which is the
   * one offered by default. Empty for an estimated mode, which has none to show:
   * the page offers no way to open the dialog on one.
   */
  itineraries = computed(() => {
    const data = this.data();
    const mode = this.mode();
    if (!data || !mode) return [];

    return (data.alternatives[mode]?.routes ?? []).map((option, index) => ({
      index,
      label: this.tipsService.alternativeLabel(mode, option.route),
      emissions: this.tipsService.fromPerPerson(option.emissionsPerPerson),
      savingsPercent: option.savingsPercent,
      route: option.route,
    }));
  });

  confirm(): void {
    const tip = this.tip();
    const mode = this.mode();
    if (tip && mode) {
      this.tipsService.chooseAlternative(tip.id, {
        mode,
        routeIndex: this.chosenRouteIndex(),
      });
    }
  }

  close(): void {
    this.tipsService.closeRoutes();
  }
}
